/**
 * Role-based access matrix, shared by server guards and UI route guards.
 * Each floor role writes to exactly one thing; everyone else only reads (PRD "Users and roles").
 */
import type { Gate, Role } from './types';

export const PERMISSIONS = [
  'plan.view',
  'plan.edit',
  'plan.publish',
  'gate.paint',
  'gate.rm',
  'gate.quality',
  'item.start',
  'dashboard.view',
  'reports.view',
  'laptop.view',
  'admin.manage',
  'audit.view',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const MATRIX: Record<Role, readonly Permission[]> = {
  planner: ['plan.view', 'plan.edit', 'plan.publish', 'dashboard.view', 'reports.view'],
  paint: ['plan.view', 'gate.paint'],
  rm: ['plan.view', 'gate.rm'],
  quality: ['plan.view', 'gate.quality'],
  supervisor: ['plan.view', 'item.start'],
  mgmt: ['plan.view', 'dashboard.view', 'reports.view', 'laptop.view'],
  admin: ['plan.view', 'dashboard.view', 'reports.view', 'laptop.view', 'admin.manage', 'audit.view'],
};

export function can(role: Role, permission: Permission): boolean {
  return MATRIX[role].includes(permission);
}

/** True when any of the user's roles grants the permission. Returns the granting role. */
export function grantingRole(roles: readonly Role[], permission: Permission): Role | null {
  return roles.find((r) => can(r, permission)) ?? null;
}

export const gatePermission = (gate: Gate): Permission => `gate.${gate}`;

/** The one home screen each role lands on after the PIN (PRD "One screen per role"). */
export const HOME_PATH: Record<Role, string> = {
  planner: '/plan',
  paint: '/paint',
  rm: '/rm',
  quality: '/quality',
  supervisor: '/run',
  mgmt: '/dashboard',
  admin: '/admin',
};

export const isGateRole = (role: Role): role is Gate =>
  role === 'paint' || role === 'rm' || role === 'quality';
